import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { formatDate } from "@/lib/format";
import { Radar, RefreshCw, Trash2, Plus } from "lucide-react";
import i18n, { uiLocale } from "@/i18n";

interface DepartureRef {
  id: string;
  code?: string | null;
  startDate: string;
  tripTitle?: string | null;
}

interface MonitoredFlightRow {
  id: string;
  tripDateId: string | null;
  direction: "outbound" | "return" | "other";
  flightNumber: string;
  flightDate: string;
  airline: string | null;
  depAirport: string | null;
  arrAirport: string | null;
  status: string | null;
  delayMinutes: number | null;
  lastCheckedAt: string | null;
  // Derived server-side from status/delay (lib/flightStatus.flightSeverity) — the single source
  // of truth for the chip colour; the client never reclassifies.
  severity: Severity;
  departure: { tripTitle?: string | null; code?: string | null; startDate?: string } | null;
}

type Severity = "ok" | "warn" | "alert";

const CHIP: Record<Severity, string> = {
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  alert: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const DIRECTION_LABEL: Record<MonitoredFlightRow["direction"], string> = {
  outbound: i18n.t("ops.monitoredFlights.direction.outbound"),
  return: i18n.t("ops.monitoredFlights.direction.return"),
  other: i18n.t("ops.monitoredFlights.direction.other"),
};

function delayText(delay: number | null): string {
  if (delay === null) return "—";
  if (delay === 0) return i18n.t("ops.monitoredFlights.onTime");
  return delay > 0 ? `+${delay} min` : `${delay} min`;
}

const FIELD =
  "focus:border-primary-500 focus:ring-primary-500/20 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white";

export default function MonitoredFlightsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [flightNumber, setFlightNumber] = useState("");
  const [flightDate, setFlightDate] = useState("");
  const [direction, setDirection] = useState<MonitoredFlightRow["direction"]>("outbound");
  const [tripDateId, setTripDateId] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["monitored-flights"],
    queryFn: () => api.get<ApiResponse<MonitoredFlightRow[]>>("/api/admin/monitored-flights"),
  });
  const { data: depData } = useQuery({
    queryKey: ["departures", "all"],
    queryFn: () => api.get<{ data: DepartureRef[] }>("/api/admin/departures?limit=200"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["monitored-flights"] });

  const addMutation = useMutation({
    mutationFn: () =>
      api.post("/api/admin/monitored-flights", {
        flightNumber,
        flightDate,
        direction,
        tripDateId: tripDateId || null,
      }),
    onSuccess: () => {
      setFlightNumber("");
      setFlightDate("");
      setTripDateId("");
      setDirection("outbound");
      invalidate();
    },
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/monitored-flights/${id}/refresh`),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/monitored-flights/${id}`),
    onSuccess: invalidate,
  });

  const rows = data?.data ?? [];
  const departures = depData?.data ?? [];
  const canAdd = flightNumber.trim().length >= 2 && /^\d{4}-\d{2}-\d{2}$/.test(flightDate);

  return (
    <div className="space-y-3">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Radar className="h-5 w-5" /> {t("ops.monitoredFlights.title")}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("ops.monitoredFlights.subtitle")}
        </p>
      </div>

      {/* Add a flight to watch */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
        <input
          className={`${FIELD} w-28`}
          placeholder={t("ops.monitoredFlights.flightNoPlaceholder")}
          value={flightNumber}
          onChange={(e) => setFlightNumber(e.target.value)}
        />
        <input
          type="date"
          className={`${FIELD} w-40`}
          value={flightDate}
          onChange={(e) => setFlightDate(e.target.value)}
        />
        <select
          className={`${FIELD} w-32`}
          value={direction}
          onChange={(e) => setDirection(e.target.value as MonitoredFlightRow["direction"])}
        >
          <option value="outbound">{DIRECTION_LABEL.outbound}</option>
          <option value="return">{DIRECTION_LABEL.return}</option>
          <option value="other">{DIRECTION_LABEL.other}</option>
        </select>
        <select
          className={`${FIELD} min-w-48 flex-1`}
          value={tripDateId}
          onChange={(e) => setTripDateId(e.target.value)}
        >
          <option value="">{t("ops.monitoredFlights.departureOptional")}</option>
          {departures.map((d) => (
            <option key={d.id} value={d.id}>
              {(d.code ? `${d.code} · ` : "") + formatDate(d.startDate)}
              {d.tripTitle ? ` · ${d.tripTitle}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!canAdd || addMutation.isPending}
          onClick={() => addMutation.mutate()}
          className="bg-primary-600 hover:bg-primary-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> {t("ops.monitoredFlights.watch")}
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        {isLoading ? (
          <p className="p-6 text-gray-500">{t("common.states.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">{t("ops.monitoredFlights.empty")}</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:bg-gray-900/40">
              <tr>
                <th className="px-4 py-3">{t("ops.monitoredFlights.colFlight")}</th>
                <th className="px-4 py-3">{t("ops.monitoredFlights.colDeparture")}</th>
                <th className="px-4 py-3">{t("ops.monitoredFlights.colStatus")}</th>
                <th className="px-4 py-3">{t("ops.monitoredFlights.colDelay")}</th>
                <th className="px-4 py-3">{t("ops.monitoredFlights.colChecked")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {rows.map((r) => {
                const sev = r.severity ?? "ok";
                return (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {r.flightNumber}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {DIRECTION_LABEL[r.direction]}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(r.flightDate)}
                        {r.depAirport && r.arrAirport ? ` · ${r.depAirport}→${r.arrAirport}` : ""}
                        {r.airline ? ` · ${r.airline}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.departure ? (
                        <>
                          <div className="text-gray-700 dark:text-gray-200">
                            {r.departure.code || formatDate(r.departure.startDate ?? r.flightDate)}
                          </div>
                          <div className="text-xs text-gray-500">{r.departure.tripTitle || ""}</div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${CHIP[sev]}`}
                      >
                        {r.status || t("ops.monitoredFlights.notChecked")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      {delayText(r.delayMinutes)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {r.lastCheckedAt ? new Date(r.lastCheckedAt).toLocaleString(uiLocale()) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title={t("ops.monitoredFlights.checkNow")}
                          disabled={refreshMutation.isPending}
                          onClick={() => refreshMutation.mutate(r.id)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-700"
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${refreshMutation.isPending && refreshMutation.variables === r.id ? "animate-spin" : ""}`}
                          />
                        </button>
                        <button
                          type="button"
                          title={t("ops.monitoredFlights.stopWatching")}
                          onClick={() => deleteMutation.mutate(r.id)}
                          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
